import { LoadingSpinner } from "components/LoadingSpinner";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { knownPracticeTypeCodeOrNull } from "constants/practiceType";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import { removeClearPatientFee } from "helpers/fee";
import {
  resolveOrderBenefits,
  resolveOrderSecondaryBenefits,
} from "helpers/orderBenefits";
import { centsToDollars } from "helpers/price";
import useDocumentTitle from "hooks/useDocumentTitle";
import { usePayerInformation } from "hooks/usePayerInformation";
import { CLEAR_HEALTH_PAYER_NAME } from "hooks/useProviderProcedure";
import { useQuery } from "hooks/useQuery";
import { isEmpty } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetOrderQuery } from "store/queries/order";
import { useSearchProviderProcedureMutation } from "store/queries/providerProcedure";
import { onSelectDraftOrder } from "store/slices/order";
import {
  setAutoCompleteValue,
  setRows,
  setSelectedProcedures,
} from "store/slices/serviceDetails";
import { IAccountResponse } from "types/Account/AccountResponse";
import {
  IOrder,
  IGFEServiceDetails,
  IDefaultServiceDetails,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

import {
  VisitForm,
  StickyFooter,
  EstimateResults,
  Submit,
  ServiceDetails,
  Communication,
  PatientBenefitsSection,
  InactiveCoverageBanner,
} from "./components";
import { CancelReviewOrder } from "./components/CancelReviewOrder";
import { DraftHeader } from "./components/DraftHeader";
import { ErrorBanner } from "./components/ErrorBanner";
import { PatientStep } from "./components/PatientStep";
import { PaymentPlan } from "./components/PaymentPlan/PaymentPlan";
import { SaveDraft } from "./components/SaveDraft";
import {
  buildSelectedProceduresArray,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./components/ServiceDetails/builders";
import {
  formatDateOfServiceForProviderProcedureSearch,
  getPaymentPlanMinDownPaymentFlatValue,
} from "./utils/helpers";
import { formatPhoneNumberToMask } from "./utils/mask";

function ReviewDraftForm({
  draftOrder,
  accountData,
  isFetching,
}: {
  draftOrder: IOrder;
  accountData: IAccountResponse | undefined;
  isFetching: boolean;
}) {
  const dispatch = useDispatch();
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>([]);
  const {
    serviceDetails,
    patient,
    procedures,
    providerProcedures,
    amountInCents,
    accountNumber,
    dateOfService,
    orderType,
    accountId,
    gfeQuote,
    gfeQuoteId,
    id,
    paymentPlan,
    outreachStopped,
    account,
  } = draftOrder;

  const { integrationErrors } = useSelector(
    (state: RootState) => state.orderSlice
  );

  const estimatedVisitCost = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      );
    }
    return centsToDollars(
      (serviceDetails as IDefaultServiceDetails).estimatedVisitCost || 0
    );
  }, [orderType, serviceDetails]);

  const { payerNameOptions } = usePayerInformation();

  const initialize = async () => {
    const autoCompleteValue = [
      ...new Set(
        serviceDetails.procedures.map(
          (procedure) =>
            `${procedure.code} - ${
              procedures?.find((p) => p?.cptCode === procedure.code.toString())
                ?.description
            }`
        )
      ),
    ];

    const selectedProcedureArrayWithDefaultDescriptions =
      buildSelectedProceduresArray({
        procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
          (procedure) => {
            const defaultDescription = procedures?.find(
              (p) => p?.cptCode === procedure.code.toString()
            )?.description;
            return {
              code: procedure.code,
              providerProcedureId: procedure.providerProcedureId,
              specialty: procedure.specialty,
              cptDescription: defaultDescription || "",
            };
          }
        ),
      });

    const selectedProcedureArray = buildSelectedProceduresArray({
      procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
        (procedure) => {
          return {
            code: procedure.code,
            providerProcedureId: procedure.providerProcedureId,
            specialty: procedure.specialty,
            cptDescription: procedure.cptDescription,
          };
        }
      ),
    });

    const selectedProceduresObject = buildSelectedProceduresObject(
      selectedProcedureArray
    );

    const rowsToSet = buildSelectedProceduresToTable({
      selectedProceduresObject,
      procedures: procedures || [],
      providerProcedures: providerProcedures || [],
      rows: [],
      // Don't pass originalProcedures here - we'll match after fetching all providers
    });

    const rowsWithoutDuplicateProviders = rowsToSet.map((row: any) => {
      const uniqueProviders = row.providerDropdown.filter(
        (provider: any, index: any, self: any) =>
          index === self.findIndex((p: any) => p.id === provider.id)
      );
      return {
        ...row,
        providerDropdown: uniqueProviders,
      };
    });

    // We'll set the correct amount after matching providers in rowsWithAllAvailableProviders
    const rowsWithCurrentAmount = rowsWithoutDuplicateProviders;

    // Build the table rows from the saved draft. `fetchedProviderProcedures` only ever
    // ADDS alternatives to each row's `providerDropdown` — selectedProvider, amount and
    // providerProcedureId always come from the saved draft — so this is safe to run with
    // an empty list first (to paint the table immediately) and again once the fetch lands.
    const buildRows = (fetchedProviderProcedures: any[]) => {
      const rowsWithAllAvailableProviders = rowsWithCurrentAmount.map((row) => {
        const providerData = [...row.providerDropdown];
        fetchedProviderProcedures.forEach((providerProcedure) => {
          if (
            row?.cptCode === providerProcedure.procedure.cptCode &&
            row.specialty === providerProcedure.provider.specialty &&
            row.procedureId === providerProcedure.procedureId &&
            !providerData.find((p) => p.id === providerProcedure.provider.id)
          ) {
            providerData.push({
              id: providerProcedure.provider.id,
              name: providerProcedure.provider.name,
              specialty: providerProcedure.provider.specialty,
              priceInCents: providerProcedure.priceInCents,
            });
          }
        });

        // Now match the provider from the original order
        const originalProcedure = (
          serviceDetails as IGFEServiceDetails
        ).procedures.find(
          (p) => p.code === row.cptCode && p.specialty === row.specialty
        );

        let { selectedProvider } = row;
        let selectedAmount = row.amount;
        let selectedProviderProcedureId = row.providerProcedureId;

        if (originalProcedure?.providerId) {
          const matchingProvider = providerData.find(
            (p) => p.id === originalProcedure.providerId?.toString()
          );

          if (matchingProvider) {
            selectedProvider = matchingProvider;
            // Use the custom amount from the original order if it exists, otherwise use provider's default price
            selectedAmount =
              originalProcedure.amountInCents !== undefined &&
              originalProcedure.amountInCents !== null
                ? originalProcedure.amountInCents
                : matchingProvider.priceInCents;

            // Find the matching providerProcedure to get the correct providerProcedureId
            const matchingProviderProcedure = fetchedProviderProcedures.find(
              (pp: any) =>
                pp.providerId === originalProcedure.providerId?.toString() &&
                pp.provider.specialty === row.specialty &&
                pp.procedure.cptCode === row.cptCode
            );
            if (matchingProviderProcedure) {
              selectedProviderProcedureId = matchingProviderProcedure.id;
            }
          }
        }

        return {
          ...row,
          providerDropdown: providerData,
          selectedProvider,
          amount: selectedAmount,
          providerProcedureId: selectedProviderProcedureId,
        };
      });

      return rowsWithAllAvailableProviders.map((row) => {
        const procedure = serviceDetails.procedures.find(
          (p) =>
            p.code === row.cptCode &&
            p.specialty === row.specialty &&
            p.providerId === row.selectedProvider.id &&
            p.providerProcedureId === row.providerProcedureId
        );
        return {
          ...row,
          benefitSelectionType:
            procedure?.benefitsForProvProc?.benefitSelectionType,
        };
      });
    };

    // Paint the table from the saved draft BEFORE going to the network. Everything above
    // is derived from data we already hold, and quantity is row multiplicity — so if we
    // wait for the fetch (as we used to) the quantity column renders unhydrated for as
    // long as the fetch takes. On a 200-unit order that was ~20 seconds (CLE-2538).
    dispatch(setRows(buildRows([])));
    dispatch(setAutoCompleteValue(autoCompleteValue));
    dispatch(
      setSelectedProcedures(selectedProcedureArrayWithDefaultDescriptions)
    );

    // One request per DISTINCT CPT code. `selectedProceduresObject` is replicated by
    // quantity (that is how quantity is stored), so mapping over it fired one identical
    // request per unit — 200 requests for a single 200-unit code (CLE-2538).
    const dateOfServiceForSearch =
      formatDateOfServiceForProviderProcedureSearch(dateOfService);
    const distinctCptCodes = [
      ...new Set(selectedProceduresObject.map((procedure) => procedure.code)),
    ];
    const fetchedProviderProcedures: any[] = await Promise.all(
      distinctCptCodes.map(async (cptCode) => {
        const result = (await searchMutation({
          accountId,
          cptCode,
          payerName:
            orderType === orderTypeConstants.GFE
              ? serviceDetails.payerName
              : CLEAR_HEALTH_PAYER_NAME,
          ...(dateOfServiceForSearch
            ? { dateOfService: dateOfServiceForSearch }
            : {}),
        })) as any;
        return result?.data?.data?.data ?? [];
      })
    ).then((results) => results.flat());

    if (!fetchedProviderProcedures.length) return;

    // Re-paint with the expanded provider dropdowns. Quantities and saved
    // provider/amount selections are unchanged — only the dropdown options grow.
    dispatch(setRows(buildRows(fetchedProviderProcedures)));
    setCurrentProviderProcedures(fetchedProviderProcedures);
  };

  const payerName = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption.name.toLowerCase() === draftOrder?.payer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, draftOrder?.payer?.name]);

  const payerName2 = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption?.name?.toLowerCase() ===
          draftOrder?.secondaryPayer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, draftOrder?.secondaryPayer?.name]);

  const sendDefaultText = useMemo(() => {
    if (
      draftOrder.scheduledFirstCommunicationDate &&
      !draftOrder.scheduledFirstCommunicationSent &&
      draftOrder.scheduledCommunicationCustomText
    ) {
      return false;
    }
    return true;
  }, [draftOrder]);

  const paymentPlanActive = useMemo(
    () => paymentPlan.find((paymentPlan) => paymentPlan.active),
    [paymentPlan]
  );

  // Calculate downpayment only flat amount with clear patient fee adjustment
  const paymentPlanMinDownPaymentFlatValue = useMemo(
    () =>
      getPaymentPlanMinDownPaymentFlatValue({
        paymentPlanMinDownPaymentFlat:
          paymentPlanActive?.paymentPlanMinDownPaymentFlat,
        orderType,
        clearPatientFeeValueInPercentage:
          draftOrder.clearPatientFeeValueInPercentage,
      }),
    [
      paymentPlanActive?.paymentPlanMinDownPaymentFlat,
      orderType,
      draftOrder.clearPatientFeeValueInPercentage,
    ]
  );

  // CLE-2635 — Athena drafts can reach approval with no gfeQuote, in which case
  // eligibility lives only in benefitDetails. Reading the quote directly here
  // seeded this form with zeros, and approveDraft prices the estimate straight
  // from these values.
  const benefits = resolveOrderBenefits(draftOrder);
  const secondaryBenefits = resolveOrderSecondaryBenefits(draftOrder);

  const methods = useForm({
    defaultValues: {
      email: patient.email,
      amount: centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      ),
      lastName: patient.lastName,
      firstName: patient.firstName,
      dateOfBirth: parseDate(patient.dateOfBirth),
      totalAmount: centsToDollars(amountInCents || 0),
      phoneNumber: patient.phoneNumber
        ? formatPhoneNumberToMask(patient.phoneNumber)
        : null,
      accountNumber,
      dateOfService: parseDate(dateOfService),
      timeOfService: parseTime(dateOfService),
      notScheduledYet: parseDate(dateOfService) === "01/01/2999",
      serviceDetails: serviceDetails.procedures,
      preferredContactMethod: patient.preferredContactMethod,
      estimatedVisitCostForPatient: estimatedVisitCost,
      orderType,
      accountId,
      payerName: payerName?.name || null,
      payerName2: payerName2?.name || null,
      payer: null,
      payer2: null,
      deductible: centsToDollars(benefits.remainingDeductibleInCents),
      outOfPocketMax: centsToDollars(benefits.outOfPocketMaxInCents),
      flatCopay: centsToDollars(benefits.flatCopayInCents),
      professionalFlatCopay: centsToDollars(
        benefits.professionalFlatCopayInCents
      ),
      showProfessionalCopay: benefits.professionalFlatCopayInCents > 0,
      coinsurancePercent: benefits.coinsurancePercent,
      deductible2: centsToDollars(secondaryBenefits.remainingDeductibleInCents),
      outOfPocketMax2: centsToDollars(secondaryBenefits.outOfPocketMaxInCents),
      flatCopay2: centsToDollars(secondaryBenefits.flatCopayInCents),
      professionalFlatCopay2: centsToDollars(
        secondaryBenefits.professionalFlatCopayInCents
      ),
      showProfessionalCopay2:
        secondaryBenefits.professionalFlatCopayInCents > 0,
      coinsurancePercent2: secondaryBenefits.coinsurancePercent,
      gfeQuoteId,
      // CLE-2661 — the order's own recorded practice type wins, so a repeat inquiry asks the
      // same question the original did — but only while it is still a code we offer. A code we
      // have since removed (notably 13, which is not a practice type at all) resolves to null
      // so the account default is used instead; carrying it forward would put the wrong code
      // on a NEW estimate, invisibly, since the dropdown renders blank for an unknown value.
      // Empty here on purpose either way: PrimaryInsuranceSection/SecondaryInsuranceSection
      // fill it once the account has loaded, which it has not reliably done at this point.
      practiceType:
        knownPracticeTypeCodeOrNull(
          draftOrder?.benefitDetails?.request?.practiceTypeCode
        ) ||
        knownPracticeTypeCodeOrNull(
          draftOrder?.serviceDetails?.practiceTypeCode
        ) ||
        null,
      practiceType2:
        knownPracticeTypeCodeOrNull(
          draftOrder?.secondaryBenefitDetails?.request?.practiceTypeCode
        ) ||
        knownPracticeTypeCodeOrNull(
          draftOrder?.serviceDetails?.secondaryPracticeTypeCode
        ) ||
        null,
      outOfPocketReason: serviceDetails?.outOfPocketReason
        ? { id: "", name: serviceDetails?.outOfPocketReason }
        : null,
      orderId: id,
      subscriberFirstName: patient.firstName,
      subscriberLastName: patient.lastName,
      subscriberDOB: parseDate(patient.dateOfBirth),
      memberId: patient?.patientBenefits?.memberID,
      memberId2: patient?.secondaryPatientBenefits?.memberID || null,
      isSubscriberPatient: patient?.patientBenefits?.isSubscriberPatient,
      paymentPlanDuration: paymentPlanActive?.paymentPlanDuration || null,
      paymentPlanDurationFull:
        (paymentPlanActive?.paymentPlanDuration || 0) + 1 || null,
      paymentPlanMinDownPaymentType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      paymentPlanMinDownPaymentFlat:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? paymentPlanMinDownPaymentFlatValue
          : null,
      paymentPlanMinDownPaymentPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      paymentPlanEnabled: !!paymentPlanActive,
      draftOrderFirstRender: true,
      outreachEnabled: !outreachStopped,
      providerDistributionLogic:
        paymentPlanActive?.providerDistributionLogic ||
        account.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityCopay: !!gfeQuote?.costApplicationRules?.facilityRules?.useCopay,
      facilityDeductible:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useDeductible,
      facilityCoinsurance:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useCoinsurance,
      professionalCopay:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCopay,
      professionalDeductible:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useDeductible,
      professionalCoinsurance:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCoinsurance,
      secondaryInsuranceActivated: !!draftOrder?.secondaryPayerId,
      facilityCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCopay,
      facilityDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useDeductible,
      facilityCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCoinsurance,
      professionalCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCopay,
      professionalDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useDeductible,
      professionalCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCoinsurance,
      benefitsUpdatedAt: gfeQuote?.benefitsUpdatedAt || null,
      secondaryBenefitsUpdatedAt:
        gfeQuote?.secondaryPatientBenefits?.benefitsUpdatedAt || null,
      // An eligibility check that errored out (common for HST-imported orders) is
      // persisted as `{ request, response: null }`. The rest of the CreateOrder flow
      // treats "no response" as "no eligibility data" — mirror that here instead of
      // handing a half-populated object to the benefits components.
      benefitDetails: draftOrder?.benefitDetails?.response
        ? draftOrder.benefitDetails
        : null,
      benefitDetails2: draftOrder?.secondaryBenefitDetails?.response
        ? draftOrder.secondaryBenefitDetails
        : null,

      ...(orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && {
        totalInsuranceAllowable:
          (serviceDetails as IPatientResponsibilityServiceDetails)
            ?.totalInsuranceAllowableInCents === null
            ? null
            : centsToDollars(
                (serviceDetails as IPatientResponsibilityServiceDetails)
                  ?.totalInsuranceAllowableInCents || 0
              ),
      }),
      scheduledFirstCommunicationDate:
        draftOrder?.scheduledFirstCommunicationDate
          ? dayjs.utc(draftOrder.scheduledFirstCommunicationDate).toDate()
          : null,
      scheduledFirstCommunicationSent:
        draftOrder?.scheduledFirstCommunicationSent || false,
      scheduledCommunicationCustomText:
        draftOrder?.scheduledCommunicationCustomText || null,
      sendDefaultText,
      initialClearPatientFeeValueInPercentage:
        draftOrder?.clearPatientFeeValueInPercentage
          ? Number(draftOrder?.clearPatientFeeValueInPercentage)
          : null,
      initialClearFeeValueInPercentage: draftOrder?.clearFeeValueInPercentage
        ? Number(draftOrder?.clearFeeValueInPercentage)
        : null,
      isDownPaymentOnly: paymentPlanActive?.isDownPaymentOnly || false,
      downpaymentOnlyType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      downpaymentOnlyPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      downpaymentOnlyFlatAmount: paymentPlanMinDownPaymentFlatValue,
      isSetAmount: paymentPlanActive?.calculationBasis === "InstallmentAmount",
      installmentFrequency:
        paymentPlanActive?.installmentFrequency || "Monthly",
      installmentAmount: paymentPlanActive?.installmentAmountInCents
        ? centsToDollars(paymentPlanActive.installmentAmountInCents)
        : null,
      accountLocationId: draftOrder?.accountLocationId ?? null,
      excludedNcciRulesetIds: gfeQuote?.excludedNcciRulesetIds || [],
      excludedNcciInfo:
        (gfeQuote?.pricingBreakdown as any)?.excludedNcciInfo || [],
    },
  });

  const integrationErrorsParsed = useMemo(() => {
    return integrationErrors
      .filter((error: any) => error.active)
      .reduce((acc: any, error: any) => {
        acc[error.errorType] = error.message;
        return acc;
      }, {} as { [key: string]: string });
  }, [integrationErrors]);

  const selectedProceduresList = useMemo(() => {
    return serviceDetails.procedures.map(
      (procedure) => `${procedure.code} - ${procedure.cptDescription}`
    );
  }, [serviceDetails]);

  useEffect(() => {
    initialize();
  }, []);

  return (
    <FormProvider {...methods}>
      {!isEmpty(integrationErrorsParsed) && (
        <ErrorBanner integrationErrors={integrationErrorsParsed} />
      )}
      {/* CLE-2803 — only renders for an estimate the monthly insurance check flagged as inactive. */}
      <InactiveCoverageBanner order={draftOrder} />
      <DraftHeader />
      <div className="flex flex-column xl:flex-row gap-3 xl:align-items-start">
        <div className="flex-1" style={{ minWidth: 0 }}>
          <PatientStep
            orderTypesVisible={accountData?.data.orderTypesVisible}
            isFetching={isFetching}
          />
          <VisitForm account={accountData?.data} isDraft order={draftOrder} />
          <PatientBenefitsSection
            draft
            account={account}
            costApplicationRules={gfeQuote?.costApplicationRules}
          />
          <ServiceDetails
            currentProviderProcedures={currentProviderProcedures}
            selectedProceduresList={selectedProceduresList}
            draft
          />
          {!!accountData?.data && (
            <PaymentPlan
              accountData={accountData}
              paymentPlan={paymentPlanActive}
              isDraft
            />
          )}
          {accountData?.data && (
            <Communication account={accountData.data} draft />
          )}
        </div>

        <EstimateResults />
      </div>

      <StickyFooter>
        <div className="flex flex-row justify-content-end gap-2">
          <div className="flex align-items-start">
            <SaveDraft />
            <CancelReviewOrder orderId={id} />
          </div>
          <Submit draftOrder />
        </div>
      </StickyFooter>
    </FormProvider>
  );
}

export function ReviewOrder() {
  useDocumentTitle("Review Estimate");
  const { draftId } = useParams();
  const query = useQuery();
  const accountId = query.get("accountId") || "";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: order, error } = useGetOrderQuery(draftId);
  const { data: accountData, isFetching } = useGetAccountByIdQuery(accountId, {
    skip: isEmpty(accountId),
  });

  const { current: toastElement } = useContext(ToastContext);

  const draftOrder = useMemo(() => {
    return order?.data as IOrder;
  }, [order]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isEmpty(draftOrder)) {
      if ((error as any)?.originalStatus === 404) {
        toastElement?.show({
          summary: "Error",
          severity: "error",
          detail: "Draft estimate not found.",
        });
        navigate(`/hospitals/view/${accountId}`);
      } else if (draftOrder.status !== orderStatusConstants.DRAFT) {
        // No longer a draft (e.g. approved since the link was shared): show the
        // estimate itself instead of an error (CLE-2979).
        navigate(`/orders/${draftId}`, { replace: true });
      } else {
        dispatch(onSelectDraftOrder(draftOrder as IOrder));
      }
    }
  }, [draftOrder]);

  if (isEmpty(draftOrder) || draftOrder.status !== orderStatusConstants.DRAFT) {
    return <LoadingSpinner />;
  }

  return (
    <ReviewDraftForm
      draftOrder={draftOrder}
      accountData={accountData}
      isFetching={isFetching}
    />
  );
}
